import { ApplicationConfig, inject, LOCALE_ID } from '@angular/core';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, Routes } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';

import { AuthService } from './shared/services/auth/auth.service';
import { canLogin, isAuthenticated, notAuthenticated } from './shared/guard/guard';
import { environment } from '../environments/environment';
import { PageKey } from './shared/services/page/models/page.key';

import { jwtDecode } from 'jwt-decode';
import { MessageService } from 'primeng/api';
import { catchError, Observable, throwError } from 'rxjs';
import { providePrimeNG } from 'primeng/config';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { ToastService } from './shared/components/toast/service/toast.service';
import { SessionStorage } from './shared/services/session-storage/session-storage.service';

registerLocaleData(localePt);

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    canActivate: [canLogin],
    data: {
      title: 'Login',
      fullscreen: true,
      key: PageKey.LOGIN,
    },
  },
  {
    path: 'profile',
    loadComponent: () => import('./pages/profile/profile.component').then(m => m.ProfileComponent),
    canActivate: [isAuthenticated],
    data: {
      title: 'Perfil',
    },
  },
  {
    path: 'forgot/password',
    loadComponent: () => import('./pages/forgot-pwd/forgot-pwd.component').then(m => m.ForgotPwdComponent),
    canActivate: [notAuthenticated],
    data: {
      title: 'Esqueci Senha',
      fullscreen: true,
    },
  },
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.component').then(m => m.HomeComponent),
    canActivate: [isAuthenticated],
    data: {
      title: 'Home',
      key: PageKey.HOME,
      search: false,
    },
  },
  {
    path: 'users',
    canMatch: [isAuthenticated],
    loadChildren: () => import('./pages/user/user.config').then(m => m.userRoute),
    data: {
      title: 'Usuários',
      key: PageKey.USERS,
    },
  },
  {
    path: 'assets',
    canMatch: [isAuthenticated],
    loadChildren: () => import('./pages/asset/asset.config').then(m => m.assetRoute),
    data: {
      title: 'Ativos',
      key: PageKey.ASSETS,
    },
  },
  {
    path: 'occurrences',
    canMatch: [isAuthenticated],
    loadChildren: () => import('./pages/occurrences/occurrences.config').then(m => m.occurrenceRoute),
    data: {
      title: 'Ocorrências',
      key: PageKey.OCCURRENCES,
    },
  },
  {
    path: 'places',
    canMatch: [isAuthenticated],
    loadChildren: () => import('./pages/places/places.config').then(m => m.placesRoute),
    data: {
      title: 'Locais',
      key: PageKey.PLACES,
    },
  },
  {
    path: 'tasks',
    canMatch: [isAuthenticated],
    loadChildren: () => import('./pages/tasks/tasks.config').then(m => m.tasksRoute),
    data: {
      title: 'Tarefas',
      key: PageKey.TASKS,
    },
  },
  {
    path: 'sectors',
    canMatch: [isAuthenticated],
    loadChildren: () => import('./pages/sectors/sectors.config').then(m => m.sectorsRoute),
    data: {
      title: 'Setores',
      key: PageKey.SECTORS,
    },
  },
  {
    path: 'events',
    canMatch: [isAuthenticated],
    loadChildren: () => import('./pages/events/events.config').then(m => m.eventsRoute),
    data: {
      title: 'Eventos',
      key: PageKey.EVENTS,
    },
  },
  {
    path: 'checklists',
    canMatch: [isAuthenticated],
    loadChildren: () => import('./pages/checklists/checklists.config').then(m => m.checklistsRoute),
    data: {
      title: 'Checklists',
      key: PageKey.CHECKLISTS,
    },
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    providePrimeNG(),
    provideHttpClient(
      withInterceptors([baseUrlInterceptor, refreshTokenInterceptor, authInterceptor, errorResponseInterceptor]),
    ),
    provideEnvironmentNgxMask(),
    BrowserAnimationsModule,
    MessageService,
    { provide: LOCALE_ID, useValue: 'pt-BR' },
  ],

};

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const token = authService.getToken();

  if (token) {
    const newReq = req.clone({
      headers: req.headers.append('Authorization', token.token),
    });

    return next(newReq);
  }

  return next(req);
}

export function refreshTokenInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const toastService = inject(ToastService);
  const sessionStorage = inject(SessionStorage);
  const token = authService.getToken();

  if (!token)
    return next(req);

  const fiveSecondsInMilliseconds = 1_000 * 5;
  const maxSafeExpiresDate = +new Date() + fiveSecondsInMilliseconds;

  if (!isTokenExpired(token.token, maxSafeExpiresDate))
    return next(req);

  if (!token.refreshToken || isTokenExpired(token.refreshToken, maxSafeExpiresDate)) {
    showSessionExpiredToast(toastService, sessionStorage);
    authService.logout();
    return next(req);
  }

  authService.refreshToken().then((value) => {
    if (!value) {
      showSessionExpiredToast(toastService, sessionStorage);
      authService.logout();
    }
  });

  return next(req);
}

export function baseUrlInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const newReq = req.clone({
    url: `${ environment.apiUrl }${ req.url }`,
  });

  return next(newReq);
}

export function errorResponseInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const toastService = inject(ToastService);
  const sessionStorage = inject(SessionStorage);

  return next(req).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          showSessionExpiredToast(toastService, sessionStorage);
          authService.logout();
        } else if (error.status === 403) {
          toastService.error('Você não tem permissão para acessar esse recurso.', 'Acesso Negado');
        }
      }
      return throwError(() => error);
    })
  );
}

export function isTokenExpired(token: string, maxExpiresDate: number): boolean {
  const jwtPayload: { exp: number } = jwtDecode(token);
  return maxExpiresDate >= +new Date(jwtPayload.exp * 1000);
}

function showSessionExpiredToast(toastService: ToastService, sessionStorage: SessionStorage): void {
  const SESSION_EXPIRED_TOAST_KEY = 'SESSION_EXPIRED_TOAST_TIME';
  const DEBOUNCE_TIME_MS = 2000;

  const lastToastTime = sessionStorage.getItem<number>(SESSION_EXPIRED_TOAST_KEY);
  const now = Date.now();

  if (!lastToastTime || now - lastToastTime > DEBOUNCE_TIME_MS) {
    sessionStorage.setItem(SESSION_EXPIRED_TOAST_KEY, now);
    toastService.warn('Sua sessão expirou. Por favor, faça login novamente.', 'Sessão Expirada');
  }
}
